import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePathname } from 'next/navigation';
import type { ExportAssetRange } from '@frameio/graphql-client/generated';
import { useActiveFolderId } from '@frameio/shared/contexts';
import { getSourceFromPath } from '@frameio/shared/integrations/utils/trackingUtils';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import {
  AssetNameRow,
  LocalCopyRow,
  RangeSelectRow,
  UploadLocationRow,
  UploadMarkersRow,
  VersionStackToggleRow,
} from '../../../shared/components/DocumentExports/ExportFormFields';
import { useOnResourceSelect } from '../../../shared/components/ExportLocation';
import {
  useActiveDocumentId,
  useActiveDocumentName,
} from '../../../shared/contexts/ActiveHostEntitiesContext';
import { usePluginFeatures } from '../../../shared/hooks/usePluginFeatures';
import { sanitizeAssetName } from '../../../shared/utils/sanitizeAssetName';
import { sanitizeFilename } from '../../../shared/utils/sanitizeFilename';
import { useIsLoadingSequenceMetadata } from '../../contexts/SequenceExportContext';
import { useExportAndUploadSequence } from '../../hooks/useExportAndUploadSequence';
import { useLastExportAssetData } from '../../hooks/useLastExportAssetData';
import { useSequenceExportSettings } from '../../hooks/useSequenceExportSettings';
import { PresetSelectRow } from './components/PresetSelectRow';
import { SequenceExportContainer } from './components/SequenceExportContainer';
import TEXT from './generated/TEXT/SequenceExport';
import { useOpenPresetManagerMutation } from './gql/mutations/generated/OpenPresetManager';
import { useExportCompleteCallback } from './hooks/useExportCompleteCallback';
import { usePresets } from './hooks/usePresets';

TEXT.DEFINE({
  /** Label for entire sequence export range option */
  entireSequence: 'Entire Sequence',
  /** Message displayed when exporting a sequence */
  exporting: (name: string) => `Exporting ${name}`,
  /** Form validation error message when no active sequence is present */
  noSequence: 'No active sequence',
  /** Tooltip text when "Add to Version Stack" is disabled */
  uploadLocationDisabledTooltip:
    'Disable "Add to Version Stack" to change location.',
  /** Label for the sequence export range option */
  sequenceInOut: 'Sequence In / Out',
  /** Button label to view export progress */
  viewProgress: 'View Progress',
  /** Default value for the asset name field when exporting a sequence */
  untitled: 'Untitled',
});

const RANGES: Record<ExportAssetRange, string> = {
  entireSequence: TEXT.entireSequence,
  sequenceInOut: TEXT.sequenceInOut,
} as const;

const RANGE_OPTIONS = Object.keys(RANGES).map((p) => ({
  value: p,
  label: RANGES[p as keyof typeof RANGES],
})) as { value: ExportAssetRange; label: string }[];

type SequenceExportProps = {
  isVersionStackMode: boolean;
  canAddToVersionStack: boolean;
  onAddToVersionStack: () => void;
  onDismiss: () => void;
  overrideFolderId?: string;
};

export const SequenceExport = ({
  onDismiss,
  isVersionStackMode,
  onAddToVersionStack,
  canAddToVersionStack,
  overrideFolderId,
}: SequenceExportProps) => {
  const [openPresetManager] = useOpenPresetManagerMutation({
    context: {
      resolveWithUXP: true,
    },
  });
  const pathname = usePathname();
  const {
    assetFolderId,
    assetFolderName,
    exportMarkersAsCommentsEnabled,
    loading: isExportSettingsLoading,
    localCopyEnabled,
    localFolderPath,
    onProjectIdChange,
    presetId,
    projectId,
    range,
    settingsError,
    storeExportSettings,
    updateExportSettings,
  } = useSequenceExportSettings({ isVersionStackMode, overrideFolderId });
  const activeSequenceId = useActiveDocumentId();
  const track = useTrackUnsafe();
  const { canManagePresets } = usePluginFeatures();
  const {
    presetOptions,
    preset,
    refetch: refetchPresets,
  } = usePresets(presetId);
  const { favorite: favoritePresets, custom: customPresets } = presetOptions;
  const sequenceName = useActiveDocumentName();
  const [exportName, setExportName] = useState<string>(
    sanitizeAssetName(sequenceName || TEXT.untitled)
  );

  useEffect(() => {
    setExportName(sanitizeAssetName(sequenceName || TEXT.untitled));
  }, [sequenceName]);

  const lastExportAssetData = useLastExportAssetData();

  const nextVersionNumber = useMemo(() => {
    if (!isVersionStackMode || !lastExportAssetData) {
      return null;
    }

    const currentVersionNumber = lastExportAssetData.versionNumber;

    return currentVersionNumber ? currentVersionNumber + 1 : 2;
  }, [isVersionStackMode, lastExportAssetData]);

  const exportAndUploadSequence = useExportAndUploadSequence();
  const onComplete = useExportCompleteCallback(isVersionStackMode);

  const activeFolderId = useActiveFolderId();
  const isLoadingSequenceMetadata = useIsLoadingSequenceMetadata();
  const { onResourceSelect } = useOnResourceSelect();

  const handleExport = useCallback(async () => {
    const filePath =
      localFolderPath && localCopyEnabled
        ? `${localFolderPath}/${sanitizeFilename(sequenceName as string)}`
        : undefined;

    track('export-started', {
      source: getSourceFromPath(pathname, 'premiere'),
      version_number: isVersionStackMode ? nextVersionNumber : 1,
      is_previously_exported: Boolean(lastExportAssetData),
      is_upload_markers_enabled: Boolean(exportMarkersAsCommentsEnabled),
      export_range: range,
      export_preset: preset?.name || '',
      encoding_format: preset?.format || '',
      codec: preset?.format || '',
    });

    const result = await exportAndUploadSequence({
      sequenceId: activeSequenceId ?? '',
      fileName: exportName.trim() || TEXT.untitled,
      presetId: preset?.id ?? '',
      fullSequence: range === 'entireSequence',
      filePath,
      folderId: assetFolderId ?? '',
      onComplete: (exportProgressItem) => {
        onComplete({
          exportProgressItem,
          exportMarkersAsCommentsEnabled: !!exportMarkersAsCommentsEnabled,
        });
      },
    });

    storeExportSettings({
      assetFolderId,
      assetFolderName,
    });
    if (result) {
      onDismiss();
    }
  }, [
    activeSequenceId,
    assetFolderId,
    assetFolderName,
    exportAndUploadSequence,
    exportMarkersAsCommentsEnabled,
    exportName,
    isVersionStackMode,
    lastExportAssetData,
    localCopyEnabled,
    localFolderPath,
    nextVersionNumber,
    onComplete,
    onDismiss,
    pathname,
    preset,
    range,
    sequenceName,
    storeExportSettings,
    track,
  ]);

  const handleSubmit = useCallback(async () => {
    onResourceSelect({
      data: { folderId: assetFolderId ?? '' },
      onSuccess: handleExport,
    });
  }, [handleExport, assetFolderId, onResourceSelect]);

  const validationErr = useMemo(() => {
    if (!activeSequenceId) {
      return TEXT.noSequence;
    }
    return settingsError;
  }, [activeSequenceId, settingsError]);

  const isLoading =
    isExportSettingsLoading ||
    (isVersionStackMode && isLoadingSequenceMetadata);

  const onUploadLocationOpen = useCallback(() => {
    track('frameio-destination-button-clicked', {
      source: getSourceFromPath(pathname, 'premiere'),
    });
  }, [track, pathname]);

  const handlePresetChange = useCallback(
    (value: string) => {
      updateExportSettings({ presetId: value });
      track('export-preset-selected', {
        export_preset: value,
        source: 'PrUXP',
      });
    },
    [updateExportSettings, track]
  );

  return (
    <>
      <SequenceExportContainer
        error={validationErr}
        format={preset?.format ?? ''}
        isEntireSequence={range === 'entireSequence'}
        isLoading={isLoading}
        onCancel={onDismiss}
        onSubmit={handleSubmit}
        presetId={preset?.id ?? ''}
      >
        {canAddToVersionStack && (
          <VersionStackToggleRow
            checked={isVersionStackMode}
            onChange={onAddToVersionStack}
          />
        )}
        <AssetNameRow
          onChange={setExportName}
          value={exportName}
          versionNumber={nextVersionNumber}
        />
        <UploadLocationRow
          disabled={isVersionStackMode}
          disabledTooltip={TEXT.uploadLocationDisabledTooltip}
          folderId={assetFolderId ?? null}
          name={assetFolderName ?? undefined}
          onFolderIdChange={(id) => {
            updateExportSettings({ assetFolderId: id });
          }}
          onOpen={onUploadLocationOpen}
          onProjectIdChange={onProjectIdChange}
          projectId={projectId}
          startInFoldersView={!!activeFolderId}
        />
        <RangeSelectRow
          onChange={(value) =>
            updateExportSettings({ range: value as ExportAssetRange })
          }
          options={RANGE_OPTIONS}
          value={range}
        />
        <PresetSelectRow
          canManagePresets={canManagePresets}
          customPresets={customPresets}
          favoritePresets={favoritePresets}
          onChange={handlePresetChange}
          onManagePresets={() => openPresetManager()}
          onRefetch={refetchPresets}
          selectedPresetId={preset?.id}
          selectedPresetName={preset?.name}
        />
        <UploadMarkersRow
          enabled={!!exportMarkersAsCommentsEnabled}
          onEnabledChange={(checked) =>
            updateExportSettings({ exportMarkersAsCommentsEnabled: checked })
          }
        />

        <LocalCopyRow
          enabled={!!localCopyEnabled}
          folderPath={localFolderPath}
          onEnabledChange={(enabled, path) => {
            updateExportSettings({
              localCopyEnabled: enabled,
              ...(path !== undefined && { localFolderPath: path }),
            });
          }}
          onFolderPathChange={(path) =>
            updateExportSettings({ localFolderPath: path })
          }
        />
      </SequenceExportContainer>
    </>
  );
};
